import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Loader from "src/Components/Common/Loader";

/**
 * Edit control beside the Branch filter: which branches to leave out of Time
 * Management by default.
 *
 * Personal, not company-wide — one person hiding a testing branch should not
 * decide what payroll sees. Saved server-side so it follows the user between
 * machines rather than living in one browser.
 *
 * A permanently-applied filter that hides payroll hours is a genuinely
 * dangerous thing to make invisible, so the count of exclusions is shown next
 * to the cog whenever any are active. Silently missing hours on a payroll
 * screen is worse than a slightly busier label.
 */
const BranchExclusionEditor = ({ branches, excludedIds, onChange, saving }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const [rect, setRect] = useState(null);

  const excludedSet = useMemo(() => new Set(excludedIds), [excludedIds]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const WIDTH = 280;
      setRect({
        top: Math.round(anchor.bottom + 6),
        left: Math.round(Math.max(8, Math.min(anchor.left - 8, window.innerWidth - WIDTH - 8))),
        width: WIDTH,
      });
    };
    place();
    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const toggle = (branchId) => {
    const next = excludedSet.has(branchId)
      ? excludedIds.filter((id) => id !== branchId)
      : [...excludedIds, branchId];
    onChange(next);
  };

  const sorted = useMemo(
    () => [...branches].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [branches]
  );

  return (
    <span ref={anchorRef} className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-label="Choose branches to always exclude"
        aria-expanded={open}
        title="Choose branches to always exclude"
        className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
          open ? "text-[#0058E5] bg-[#EAF3FF]" : "text-[#868DA6] hover:text-[#1A2332] hover:bg-[#F0F1F5]"
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M10.94 2.44a1.5 1.5 0 0 1 2.12 0l.5.5a1.5 1.5 0 0 1 0 2.12l-7 7a1.5 1.5 0 0 1-.67.39l-2.98.79.79-2.98a1.5 1.5 0 0 1 .39-.67l6.85-7.15Z"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinejoin="round"
          />
          <path d="M10.1 3.3l2.6 2.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      </button>
      {excludedIds.length > 0 ? (
        <span
          title="These branches are hidden from this screen"
          className="px-1.5 py-[1px] rounded-[4px] bg-[#FEF3C7] text-[#92400E] text-[11px] leading-4 font-OpenSansSemiBold whitespace-nowrap"
        >
          {excludedIds.length} excluded
        </span>
      ) : null}

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              style={{ top: rect.top, left: rect.left, width: rect.width }}
              className="fixed z-[60] rounded-[8px] border border-[#C9CEE2] bg-white shadow-[0px_8px_24px_rgba(16,24,40,0.12)]"
            >
              <div className="px-3 pt-2.5 pb-2 border-b border-[#EEF0F9]">
                <p className="font-OpenSansSemiBold text-[13px] leading-[18px] text-[#1A2332]">Always exclude</p>
                <p className="mt-0.5 font-OpenSansRegular text-[12px] leading-[16px] text-[#868DA6]">
                  Ticked branches are left out of this screen, including when the filter says All Branches.
                </p>
              </div>

              <div className="max-h-[240px] overflow-y-auto py-1">
                {sorted.length === 0 ? (
                  <p className="px-3 py-3 font-OpenSansRegular text-[13px] text-[#868DA6]">No branches found.</p>
                ) : (
                  sorted.map((branch) => {
                    const checked = excludedSet.has(branch.id);
                    return (
                      <button
                        key={branch.id}
                        type="button"
                        onClick={() => toggle(branch.id)}
                        className="w-full px-3 py-[7px] flex items-center gap-2 text-left hover:bg-[#EEF0F9]"
                      >
                        {checked ? (
                          <span className="w-[16px] h-[16px] shrink-0 rounded-[4px] bg-[#0065FD] flex items-center justify-center">
                            <svg width="10" height="8" viewBox="0 0 11 9" fill="none" aria-hidden="true">
                              <path
                                d="M1 4.5L4 7.5L10 1.5"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        ) : (
                          <span className="w-[16px] h-[16px] shrink-0 rounded-[4px] bg-white border border-[#C9CEE2]" />
                        )}
                        <span className="flex-1 font-OpenSansRegular text-[13px] leading-[18px] text-[#1A2332] truncate">
                          {branch.name}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="px-3 py-2 border-t border-[#EEF0F9] flex items-center justify-between gap-2">
                <span className="font-OpenSansRegular text-[12px] leading-[16px] text-[#868DA6]">
                  {saving ? "Saving..." : "Saved to your account"}
                </span>
                {saving ? <Loader variant="inline" ariaLabel="saving-preferences" /> : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

export default BranchExclusionEditor;
